import React, { useState, useMemo } from "react";
import { 
  Merge, 
  Layers, 
  X, 
  Eye,
  Search,
  Percent,
  Filter,
  Loader2,
  Download,
  CheckSquare,
  Square,
  Trash2
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import ImageThumbnail from "@/components/common/ImageThumbnail";
import { formatCurrency } from "@/utils/formatters";
import CompanySettingsCard from "@/components/maintenance/CompanySettingsCard";
import api from "@/lib/axios";
import { toast } from "sonner";

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Get similarity icon based on percentage
 * @param {number} percent - Similarity percentage (0-100)
 * @returns {string} Emoji icon representing similarity level
 * @example
 * getSimilarityIcon(95) // returns '🟢'
 * getSimilarityIcon(75) // returns '🟡'
 * getSimilarityIcon(50) // returns '🟠'
 */
const getSimilarityIcon = (percent) => {
  if (percent >= 90) return '🟢';
  if (percent >= 70) return '🟡';
  return '🟠';
};

/**
 * Get CSS color class based on adjustment percentage
 * @param {number} percent - Adjustment percentage (negative = discount, positive = increase)
 * @returns {string} Tailwind CSS color class
 * @example
 * getAdjustmentColor(10)  // returns 'text-red-600' (price increase)
 * getAdjustmentColor(-5)  // returns 'text-green-600' (discount)
 * getAdjustmentColor(0)   // returns 'text-slate-600' (no change)
 */
const getAdjustmentColor = (percent) => {
  if (percent > 0) return 'text-red-600';
  if (percent < 0) return 'text-green-600';
  return 'text-slate-600';
};

/**
 * Get singular or plural suffix based on count
 * @param {number} count - The number to check
 * @param {string} singular - Suffix for count === 1
 * @param {string} plural - Suffix for count !== 1
 * @returns {string} The appropriate suffix
 * @example
 * pluralize(1, '', 's')   // returns ''
 * pluralize(5, '', 's')   // returns 's'
 * pluralize(0, 'item', 'items') // returns 'items'
 */
const pluralize = (count, singular, plural) => count === 1 ? singular : plural;

/**
 * MaintenanceView - Maintenance zone for merging types and adjusting project costs
 */
const MaintenanceView = ({
  // Maintenance types data
  maintenanceTypes,
  maintenanceSearchQuery,
  setMaintenanceSearchQuery,
  
  // Merge state
  selectedMergeSource,
  setSelectedMergeSource,
  selectedMergeTarget,
  setSelectedMergeTarget,
  handleMergeTypes,
  
  // Similar types
  similarTypes,
  setSimilarTypes,
  isLoadingSimilar,
  findSimilarTypes,
  
  // Project management
  maintenanceProjects,
  selectedProject,
  setSelectedProject,
  projectAdjustPercent,
  setProjectAdjustPercent,
  handleAdjustProjectCosts,
  isAdjustingProject,
  
  // Actions
  openImagePreview,
  toast: toastProp,
  
  // Refresh callback
  refreshMaintenanceData
}) => {
  // Additional filter state
  const [filterMaterial, setFilterMaterial] = useState("all");
  const [filterColor, setFilterColor] = useState("all");
  const [filterSeries, setFilterSeries] = useState("all");
  const [filterFactory, setFilterFactory] = useState("all");
  const [showFilters, setShowFilters] = useState(false);
  
  // Bulk selection state
  const [selectedTypes, setSelectedTypes] = useState(new Set());
  const [bulkMergeTarget, setBulkMergeTarget] = useState(null);
  const [showBulkMergeDialog, setShowBulkMergeDialog] = useState(false);
  const [isBulkMerging, setIsBulkMerging] = useState(false);
  const [isExporting, setIsExporting] = useState(false);

  // Extract unique values for filter options
  const filterOptions = useMemo(() => {
    const materials = new Set();
    const colors = new Set();
    const series = new Set();
    const factories = new Set();

    maintenanceTypes.forEach(type => {
      if (type.material) materials.add(type.material);
      if (type.color) colors.add(type.color);
      if (type.series) series.add(type.series);
      if (type.factory_name) factories.add(type.factory_name);
    });

    return {
      materials: [...materials].sort(),
      colors: [...colors].sort(),
      series: [...series].sort(),
      factories: [...factories].sort()
    };
  }, [maintenanceTypes]);

  // Filter maintenance types based on all filters
  const filteredMaintenanceTypes = useMemo(() => {
    return maintenanceTypes.filter(type => {
      // Search query filter
      const matchesSearch = !maintenanceSearchQuery || 
        type.name.toLowerCase().includes(maintenanceSearchQuery.toLowerCase()) ||
        (type.material && type.material.toLowerCase().includes(maintenanceSearchQuery.toLowerCase())) ||
        (type.color && type.color.toLowerCase().includes(maintenanceSearchQuery.toLowerCase())) ||
        (type.series && type.series.toLowerCase().includes(maintenanceSearchQuery.toLowerCase())) ||
        (type.factory_name && type.factory_name.toLowerCase().includes(maintenanceSearchQuery.toLowerCase()));
      
      // Material filter
      const matchesMaterial = filterMaterial === "all" || type.material === filterMaterial;
      
      // Color filter
      const matchesColor = filterColor === "all" || type.color === filterColor;
      
      // Series filter
      const matchesSeries = filterSeries === "all" || type.series === filterSeries;
      
      // Factory filter
      const matchesFactory = filterFactory === "all" || type.factory_name === filterFactory;
      
      return matchesSearch && matchesMaterial && matchesColor && matchesSeries && matchesFactory;
    });
  }, [maintenanceTypes, maintenanceSearchQuery, filterMaterial, filterColor, filterSeries, filterFactory]);

  // Count active filters
  const activeFilterCount = [filterMaterial, filterColor, filterSeries, filterFactory]
    .filter(f => f !== "all").length;

  // Clear all filters
  const clearFilters = () => {
    setMaintenanceSearchQuery("");
    setFilterMaterial("all");
    setFilterColor("all");
    setFilterSeries("all");
    setFilterFactory("all");
    setSelectedTypes(new Set());
  };

  // Toggle type selection for bulk operations
  const toggleTypeSelection = (typeId) => {
    setSelectedTypes(prev => {
      const newSet = new Set(prev);
      if (newSet.has(typeId)) {
        newSet.delete(typeId);
      } else {
        newSet.add(typeId);
      }
      return newSet;
    });
  };

  // Select all filtered types
  const selectAllFiltered = () => {
    const allIds = new Set(filteredMaintenanceTypes.map(t => t.id));
    setSelectedTypes(allIds);
  };

  // Clear all selections
  const clearSelection = () => {
    setSelectedTypes(new Set());
    setBulkMergeTarget(null);
  };

  // Handle bulk merge
  const handleBulkMerge = async () => {
    if (!bulkMergeTarget || selectedTypes.size === 0) return;
    
    // Remove target from sources if accidentally selected
    const sourceIds = [...selectedTypes].filter(id => id !== bulkMergeTarget.id);
    
    if (sourceIds.length === 0) {
      toast.error("Selecciona al menos un tipo origen diferente al destino");
      return;
    }
    
    setIsBulkMerging(true);
    try {
      // Merge each source into target sequentially
      for (const sourceId of sourceIds) {
        await api.post(`${API}/api/maintenance/merge-types`, {
          source_id: sourceId,
          target_id: bulkMergeTarget.id
        });
      }
      
      toast.success(`${sourceIds.length} tipos fusionados exitosamente en "${bulkMergeTarget.name}"`);
      setShowBulkMergeDialog(false);
      clearSelection();
      
      // Refresh data
      if (refreshMaintenanceData) {
        refreshMaintenanceData();
      }
    } catch (error) {
      console.error("Bulk merge error:", error);
      toast.error(error.response?.data?.detail || "Error al fusionar tipos");
    } finally {
      setIsBulkMerging(false);
    }
  };

  // Export filtered types to Excel
  const handleExportFiltered = async () => {
    if (filteredMaintenanceTypes.length === 0) {
      toast.error("No hay tipos para exportar");
      return;
    }
    
    setIsExporting(true);
    try {
      // Prepare data for export
      const exportData = filteredMaintenanceTypes.map(type => ({
        name: type.name,
        material: type.material || "",
        color: type.color || "",
        series: type.series || "",
        factory_name: type.factory_name || "",
        entries_count: type.entries_count || 0,
        average_cost_per_m2: type.average_cost_per_m2 || 0,
        total_m2: type.total_m2 || 0
      }));
      
      // Create CSV content
      const headers = ["Nombre", "Material", "Color", "Serie", "Fábrica", "Muestras", "Costo Prom./m²", "Total m²"];
      const csvContent = [
        headers.join(","),
        ...exportData.map(row => [
          `"${row.name}"`,
          `"${row.material}"`,
          `"${row.color}"`,
          `"${row.series}"`,
          `"${row.factory_name}"`,
          row.entries_count,
          row.average_cost_per_m2.toFixed(2),
          row.total_m2.toFixed(2)
        ].join(","))
      ].join("\n");
      
      // Download file
      const blob = new Blob(["\uFEFF" + csvContent], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `tipos_mantenimiento_${new Date().toISOString().split("T")[0]}.csv`;
      link.click();
      URL.revokeObjectURL(url);
      
      toast.success(`${filteredMaintenanceTypes.length} tipos exportados a CSV`);
    } catch (error) {
      console.error("Export error:", error);
      toast.error("Error al exportar");
    } finally {
      setIsExporting(false);
    }
  };

  // Get selected types data
  const selectedTypesData = useMemo(() => {
    return filteredMaintenanceTypes.filter(t => selectedTypes.has(t.id));
  }, [filteredMaintenanceTypes, selectedTypes]);

  return (
    <div className="space-y-8" data-testid="maintenance-view">
      {/* Merge Types Card */}
      <Card className="metric-card">
        <CardHeader className="border-b border-slate-100 p-4">
          <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
            <Merge className="w-5 h-5" />
            Fusionar Tipos Duplicados
          </CardTitle>
          <CardDescription className="text-slate-500">
            Selecciona dos tipos para fusionarlos. Las cotizaciones del tipo origen se moverán al tipo destino.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Source Type Selection */}
            <div className="space-y-4">
              <Label className="text-sm font-semibold text-slate-700">Tipo Origen (será eliminado)</Label>
              <div className="border rounded-sm p-4 bg-red-50 min-h-[120px]">
                {selectedMergeSource ? (
                  <div className="flex items-start gap-3">
                    <ImageThumbnail
                      src={selectedMergeSource.image_data}
                      alt={selectedMergeSource.name}
                      className="w-16 h-16 rounded border border-slate-200 flex-shrink-0"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{selectedMergeSource.name}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">{selectedMergeSource.material}</Badge>
                        {selectedMergeSource.series && <Badge variant="outline" className="text-xs">{selectedMergeSource.series}</Badge>}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{selectedMergeSource.entries_count} cotizaciones</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedMergeSource(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-slate-400 text-center py-6">Selecciona el tipo a eliminar de la lista</p>
                )}
              </div>
            </div>

            {/* Target Type Selection */}
            <div className="space-y-4">
              <Label className="text-sm font-semibold text-slate-700">Tipo Destino (recibirá las cotizaciones)</Label>
              <div className="border rounded-sm p-4 bg-emerald-50 min-h-[120px]">
                {selectedMergeTarget ? (
                  <div className="flex items-start gap-3">
                    <ImageThumbnail
                      src={selectedMergeTarget.image_data}
                      alt={selectedMergeTarget.name}
                      className="w-16 h-16 rounded border border-slate-200 flex-shrink-0"
                    />
                    <div className="flex-1">
                      <p className="font-medium text-slate-900">{selectedMergeTarget.name}</p>
                      <div className="flex gap-2 mt-1">
                        <Badge variant="secondary" className="text-xs">{selectedMergeTarget.material}</Badge>
                        {selectedMergeTarget.series && <Badge variant="outline" className="text-xs">{selectedMergeTarget.series}</Badge>}
                      </div>
                      <p className="text-sm text-slate-500 mt-1">{selectedMergeTarget.entries_count} cotizaciones</p>
                    </div>
                    <Button variant="ghost" size="sm" onClick={() => setSelectedMergeTarget(null)}>
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                ) : (
                  <p className="text-slate-400 text-center py-6">Selecciona el tipo que conservará todo</p>
                )}
              </div>
            </div>
          </div>

          {/* Merge Button */}
          <div className="flex justify-center mt-6">
            <Button
              onClick={handleMergeTypes}
              disabled={!selectedMergeSource || !selectedMergeTarget}
              className="px-8"
              data-testid="merge-types-btn"
            >
              <Merge className="w-4 h-4 mr-2" />
              Fusionar Tipos
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Types List for Selection */}
      <Card className="metric-card">
        <CardHeader className="border-b border-slate-100 p-4">
          <div className="flex items-center justify-between">
            <div>
              <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
                <Layers className="w-5 h-5" />
                Seleccionar Tipos
              </CardTitle>
              <CardDescription className="text-slate-500">
                Haz clic en un tipo para seleccionarlo como origen o destino, o usa las casillas para selección múltiple
              </CardDescription>
            </div>
            <div className="flex gap-2">
              <Button
                variant="outline"
                size="sm"
                onClick={handleExportFiltered}
                disabled={isExporting || filteredMaintenanceTypes.length === 0}
                data-testid="export-filtered-btn"
              >
                {isExporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Exportar ({filteredMaintenanceTypes.length})
              </Button>
            </div>
          </div>
        </CardHeader>
        <CardContent className="p-0">
          {/* Search and Filters */}
          <div className="p-4 border-b space-y-4">
            {/* Search + Filter Toggle */}
            <div className="flex gap-2">
              <div className="relative flex-1">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                <Input
                  placeholder="Buscar por nombre, material, color, serie, fábrica..."
                  value={maintenanceSearchQuery}
                  onChange={(e) => setMaintenanceSearchQuery(e.target.value)}
                  className="pl-9"
                  data-testid="maintenance-search-input"
                />
                {maintenanceSearchQuery && (
                  <Button
                    variant="ghost"
                    size="sm"
                    className="absolute right-1 top-1/2 -translate-y-1/2 h-7 w-7 p-0"
                    onClick={() => setMaintenanceSearchQuery("")}
                  >
                    <X className="w-3 h-3" />
                  </Button>
                )}
              </div>
              <Button
                variant={showFilters ? "default" : "outline"}
                size="sm"
                onClick={() => setShowFilters(!showFilters)}
                className="relative"
              >
                <Filter className="w-4 h-4 mr-2" />
                Filtros
                {activeFilterCount > 0 && (
                  <Badge variant="destructive" className="ml-2 h-5 w-5 p-0 flex items-center justify-center text-xs">
                    {activeFilterCount}
                  </Badge>
                )}
              </Button>
              {(activeFilterCount > 0 || maintenanceSearchQuery) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={clearFilters}
                  className="text-slate-500"
                >
                  <X className="w-4 h-4 mr-1" />
                  Limpiar
                </Button>
              )}
            </div>

            {/* Filter Dropdowns */}
            {showFilters && (
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3 p-3 bg-slate-50 rounded-lg">
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Material</Label>
                  <Select value={filterMaterial} onValueChange={setFilterMaterial}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los materiales</SelectItem>
                      {filterOptions.materials.map(m => (
                        <SelectItem key={m} value={m}>{m}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Color</Label>
                  <Select value={filterColor} onValueChange={setFilterColor}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Todos" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todos los colores</SelectItem>
                      {filterOptions.colors.map(c => (
                        <SelectItem key={c} value={c}>{c}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Serie</Label>
                  <Select value={filterSeries} onValueChange={setFilterSeries}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las series</SelectItem>
                      {filterOptions.series.map(s => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="space-y-1">
                  <Label className="text-xs text-slate-500">Fábrica</Label>
                  <Select value={filterFactory} onValueChange={setFilterFactory}>
                    <SelectTrigger className="h-9">
                      <SelectValue placeholder="Todas" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="all">Todas las fábricas</SelectItem>
                      {filterOptions.factories.map(f => (
                        <SelectItem key={f} value={f}>{f}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            )}

            {/* Results count */}
            <div className="flex items-center justify-between text-sm text-slate-500">
              <span>
                Mostrando {filteredMaintenanceTypes.length} de {maintenanceTypes.length} tipos
              </span>
              {(activeFilterCount > 0 || maintenanceSearchQuery) && (
                <span className="text-blue-600">
                  {maintenanceTypes.length - filteredMaintenanceTypes.length} ocultos por filtros
                </span>
              )}
            </div>

            {/* Bulk Selection Actions */}
            {selectedTypes.size > 0 && (
              <div className="flex items-center justify-between p-3 bg-blue-50 rounded-lg border border-blue-200">
                <div className="flex items-center gap-3">
                  <CheckSquare className="w-5 h-5 text-blue-600" />
                  <span className="font-medium text-blue-900">
                    {selectedTypes.size} tipo{pluralize(selectedTypes.size, '', 's')} seleccionado{pluralize(selectedTypes.size, '', 's')}
                  </span>
                </div>
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={clearSelection}
                    className="text-slate-600"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Deseleccionar
                  </Button>
                  <Button
                    variant="default"
                    size="sm"
                    onClick={() => setShowBulkMergeDialog(true)}
                    disabled={selectedTypes.size < 2}
                    className="bg-blue-600 hover:bg-blue-700"
                  >
                    <Merge className="w-4 h-4 mr-2" />
                    Fusión Masiva
                  </Button>
                </div>
              </div>
            )}
          </div>
          
          {maintenanceTypes.length === 0 ? (
            <div className="empty-state">
              <Layers className="empty-state-icon" />
              <p className="text-slate-600 font-medium">Sin tipos indexados</p>
            </div>
          ) : (
            <ScrollArea className="h-[400px]">
              {/* Select All Header */}
              <div className="flex items-center gap-3 p-3 bg-slate-50 border-b sticky top-0 z-10">
                <Checkbox
                  checked={selectedTypes.size === filteredMaintenanceTypes.length && filteredMaintenanceTypes.length > 0}
                  onCheckedChange={(checked) => {
                    if (checked) {
                      selectAllFiltered();
                    } else {
                      clearSelection();
                    }
                  }}
                  data-testid="select-all-checkbox"
                />
                <span className="text-sm text-slate-600">
                  {selectedTypes.size > 0 
                    ? `${selectedTypes.size} seleccionado${selectedTypes.size > 1 ? 's' : ''}`
                    : "Seleccionar todos"
                  }
                </span>
              </div>
              
              <div className="divide-y">
                {filteredMaintenanceTypes.map((type) => {
                  const isSource = selectedMergeSource?.id === type.id;
                  const isTarget = selectedMergeTarget?.id === type.id;
                  const isSelected = selectedTypes.has(type.id);
                  return (
                    <div
                      key={type.id}
                      className={`flex items-center gap-4 p-4 hover:bg-slate-50 transition-colors
                        ${isSource ? 'bg-red-50 border-l-4 border-red-500' : ''}
                        ${isTarget ? 'bg-emerald-50 border-l-4 border-emerald-500' : ''}
                        ${isSelected && !isSource && !isTarget ? 'bg-blue-50' : ''}
                      `}
                    >
                      {/* Checkbox for bulk selection */}
                      <Checkbox
                        checked={isSelected}
                        onCheckedChange={() => toggleTypeSelection(type.id)}
                        className="flex-shrink-0"
                        data-testid={`checkbox-${type.id}`}
                      />
                      
                      <ImageThumbnail
                        src={type.image_data}
                        alt={type.name}
                        className="w-12 h-12 rounded border border-slate-200 flex-shrink-0 cursor-pointer"
                        onClick={() => openImagePreview && openImagePreview(type.image_data)}
                      />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-slate-900 truncate">{type.name}</p>
                        <div className="flex gap-2 mt-1 flex-wrap">
                          <Badge variant="secondary" className="text-xs">{type.material}</Badge>
                          {type.series && <Badge variant="outline" className="text-xs">{type.series}</Badge>}
                          {type.color && <Badge variant="outline" className="text-xs">{type.color}</Badge>}
                          {type.factory_name && <Badge variant="outline" className="text-xs bg-purple-50">{type.factory_name}</Badge>}
                        </div>
                      </div>
                      <div className="text-right">
                        <p className="text-sm font-mono">{type.entries_count} muestras</p>
                        <p className="text-sm text-slate-500">{formatCurrency(type.average_cost_per_m2)}/m²</p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          variant={isSource ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedMergeSource(isSource ? null : type)}
                          className={isSource ? "bg-red-600 hover:bg-red-700" : "text-red-600 border-red-300 hover:bg-red-50"}
                          data-testid={`select-source-${type.id}`}
                        >
                          {isSource ? "✓ Origen" : "Origen"}
                        </Button>
                        <Button
                          variant={isTarget ? "default" : "outline"}
                          size="sm"
                          onClick={() => setSelectedMergeTarget(isTarget ? null : type)}
                          className={isTarget ? "bg-emerald-600 hover:bg-emerald-700" : "text-emerald-600 border-emerald-300 hover:bg-emerald-50"}
                          data-testid={`select-target-${type.id}`}
                        >
                          {isTarget ? "✓ Destino" : "Destino"}
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => {
                            setSelectedMergeSource(type);
                            findSimilarTypes(type.id);
                          }}
                          className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                          disabled={!type.image_hash}
                          title={type.image_hash ? "Buscar tipos similares por imagen" : "Sin imagen para comparar"}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>
            </ScrollArea>
          )}
        </CardContent>
      </Card>

      {/* Similar Types Results */}
      {(similarTypes.length > 0 || isLoadingSimilar) && (
        <Card className="metric-card">
          <CardHeader className="border-b border-slate-100 p-4">
            <div className="flex items-center justify-between">
              <div>
                <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
                  <Eye className="w-5 h-5" />
                  Tipos Similares {selectedMergeSource && `a "${selectedMergeSource.name}"`}
                </CardTitle>
                <CardDescription className="text-slate-500">
                  Tipos con imágenes similares para posible fusión
                </CardDescription>
              </div>
              <Button variant="ghost" size="sm" onClick={() => setSimilarTypes([])}>
                <X className="w-4 h-4" />
              </Button>
            </div>
          </CardHeader>
          <CardContent className="p-4">
            {isLoadingSimilar ? (
              <div className="flex items-center justify-center h-32">
                <div className="w-8 h-8 border-4 border-slate-200 border-t-slate-900 rounded-full animate-spin" />
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Source type */}
                {selectedMergeSource && (
                  <div className="border-2 border-blue-500 rounded-lg p-4 bg-blue-50">
                    <div className="text-sm font-semibold text-blue-700 mb-2">TIPO ORIGINAL</div>
                    <div className="flex gap-4">
                      <ImageThumbnail
                        src={selectedMergeSource.image_data}
                        alt={selectedMergeSource.name}
                        className="w-24 h-24 rounded border border-slate-200"
                        onClick={() => selectedMergeSource.image_data && openImagePreview(selectedMergeSource.image_data, selectedMergeSource.name)}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{selectedMergeSource.name}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="secondary">{selectedMergeSource.material}</Badge>
                          {selectedMergeSource.series && <Badge variant="outline">{selectedMergeSource.series}</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{selectedMergeSource.entries_count} muestras</p>
                        <p className="text-sm font-mono">{formatCurrency(selectedMergeSource.average_cost_per_m2)}/m²</p>
                      </div>
                    </div>
                  </div>
                )}
                
                {/* Similar types */}
                {similarTypes.map((type) => (
                  <div key={type.id} className="border rounded-lg p-4 hover:bg-slate-50 transition-colors">
                    <div className="flex items-center justify-between mb-2">
                      <div className="text-sm font-semibold text-slate-700">
                        {getSimilarityIcon(type.similarity_percent)} 
                        {type.similarity_percent}% similar
                      </div>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => {
                          setSelectedMergeTarget(type);
                          if (toast) toast.info(`"${type.name}" seleccionado como destino`);
                        }}
                        className="text-emerald-600 border-emerald-300"
                      >
                        Seleccionar Destino
                      </Button>
                    </div>
                    <div className="flex gap-4">
                      <ImageThumbnail
                        src={type.image_data}
                        alt={type.name}
                        className="w-24 h-24 rounded border border-slate-200"
                        onClick={() => type.image_data && openImagePreview(type.image_data, type.name)}
                      />
                      <div className="flex-1">
                        <p className="font-medium">{type.name}</p>
                        <div className="flex gap-2 mt-1">
                          <Badge variant="secondary">{type.material}</Badge>
                          {type.series && <Badge variant="outline">{type.series}</Badge>}
                        </div>
                        <p className="text-sm text-slate-500 mt-1">{type.entries_count} muestras</p>
                        <p className="text-sm font-mono">{formatCurrency(type.average_cost_per_m2)}/m²</p>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Project Cost Management */}
      <Card className="metric-card">
        <CardHeader className="border-b border-slate-100 p-4">
          <CardTitle className="text-lg font-medium text-slate-900 flex items-center gap-2">
            <Percent className="w-5 h-5" />
            Ajustar Costos por Proyecto
          </CardTitle>
          <CardDescription className="text-slate-500">
            Modifica el porcentaje de ajuste aplicado a los costos de un proyecto de fábrica.
          </CardDescription>
        </CardHeader>
        <CardContent className="p-6">
          {maintenanceProjects.length === 0 ? (
            <p className="text-slate-400 text-center py-8">No hay proyectos con costos de fábrica</p>
          ) : (
            <div className="space-y-4">
              {/* Project Selection */}
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="md:col-span-2">
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Seleccionar Proyecto</Label>
                  <Select
                    value={selectedProject?.project_name || ""}
                    onValueChange={(value) => {
                      const proj = maintenanceProjects.find(p => p.project_name === value);
                      setSelectedProject(proj || null);
                      setProjectAdjustPercent(proj?.adjustment_percent?.toString() || "");
                    }}
                  >
                    <SelectTrigger className="w-full" data-testid="project-select">
                      <SelectValue placeholder="Selecciona un proyecto..." />
                    </SelectTrigger>
                    <SelectContent>
                      {maintenanceProjects.map((proj) => (
                        <SelectItem key={proj.project_name} value={proj.project_name}>
                          {proj.project_name} ({proj.entries_count} elementos)
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-sm font-semibold text-slate-700 mb-2 block">Nuevo % Ajuste</Label>
                  <div className="flex items-center gap-2">
                    <Input
                      type="number"
                      step="0.1"
                      className="font-mono"
                      placeholder="Ej: 15, -5"
                      value={projectAdjustPercent}
                      onChange={(e) => setProjectAdjustPercent(e.target.value)}
                      data-testid="project-adjust-percent"
                    />
                    <span className="text-lg font-bold text-slate-400">%</span>
                  </div>
                </div>
              </div>

              {/* Selected Project Info */}
              {selectedProject && (
                <div className="bg-slate-50 rounded-lg p-4 border">
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Elementos</p>
                      <p className="text-lg font-semibold">{selectedProject.entries_count}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Tipos</p>
                      <p className="text-lg font-semibold">{selectedProject.types_count}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">Promedio $/m²</p>
                      <p className="text-lg font-semibold text-emerald-600">${selectedProject.avg_cost_per_m2}</p>
                    </div>
                    <div>
                      <p className="text-xs text-slate-500 uppercase">% Ajuste Actual</p>
                      <p className={`text-lg font-semibold ${getAdjustmentColor(selectedProject.adjustment_percent)}`}>
                        {selectedProject.adjustment_percent ? `${selectedProject.adjustment_percent > 0 ? '+' : ''}${selectedProject.adjustment_percent}%` : '0%'}
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {/* Action Button */}
              <div className="flex justify-end gap-2">
                <Button
                  onClick={handleAdjustProjectCosts}
                  disabled={!selectedProject || !projectAdjustPercent || isAdjustingProject}
                  className={parseFloat(projectAdjustPercent) > 0 ? 'bg-red-600 hover:bg-red-700' : 'bg-emerald-600 hover:bg-emerald-700'}
                  data-testid="apply-project-adjust"
                >
                  {isAdjustingProject ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin mr-2" />
                      Aplicando...
                    </>
                  ) : (
                    <>
                      <Percent className="w-4 h-4 mr-2" />
                      Aplicar Nuevo Ajuste
                    </>
                  )}
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Company Settings Card */}
      <CompanySettingsCard />

      {/* Bulk Merge Dialog */}
      <AlertDialog open={showBulkMergeDialog} onOpenChange={setShowBulkMergeDialog}>
        <AlertDialogContent className="max-w-2xl">
          <AlertDialogHeader>
            <AlertDialogTitle className="flex items-center gap-2">
              <Merge className="w-5 h-5" />
              Fusión Masiva de Tipos
            </AlertDialogTitle>
            <AlertDialogDescription>
              Vas a fusionar {selectedTypes.size} tipos en uno solo. Selecciona el tipo destino que conservará toda la información.
            </AlertDialogDescription>
          </AlertDialogHeader>
          
          <div className="space-y-4 py-4">
            {/* Target Selection */}
            <div className="space-y-2">
              <Label className="font-semibold">Tipo Destino (conservará todo)</Label>
              <Select 
                value={bulkMergeTarget?.id || ""} 
                onValueChange={(value) => {
                  const target = selectedTypesData.find(t => t.id === value);
                  setBulkMergeTarget(target);
                }}
              >
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Selecciona el tipo que mantendrá las cotizaciones" />
                </SelectTrigger>
                <SelectContent>
                  {selectedTypesData.map(type => (
                    <SelectItem key={type.id} value={type.id}>
                      <div className="flex items-center gap-2">
                        <span className="font-medium">{type.name}</span>
                        <Badge variant="secondary" className="text-xs">{type.entries_count} muestras</Badge>
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            {/* Preview */}
            {bulkMergeTarget && (
              <div className="p-4 bg-slate-50 rounded-lg space-y-3">
                <p className="text-sm font-medium text-slate-700">Vista previa de la fusión:</p>
                <div className="flex items-center gap-2 text-emerald-700 bg-emerald-50 p-2 rounded">
                  <CheckSquare className="w-4 h-4" />
                  <span className="font-medium">{bulkMergeTarget.name}</span>
                  <span className="text-sm">(destino - se conserva)</span>
                </div>
                <div className="space-y-1">
                  {selectedTypesData
                    .filter(t => t.id !== bulkMergeTarget.id)
                    .map(type => (
                      <div key={type.id} className="flex items-center gap-2 text-red-700 bg-red-50 p-2 rounded">
                        <Trash2 className="w-4 h-4" />
                        <span>{type.name}</span>
                        <span className="text-sm">({type.entries_count} cotizaciones se moverán)</span>
                      </div>
                    ))
                  }
                </div>
                <p className="text-sm text-slate-500 mt-2">
                  Total de cotizaciones a mover: {selectedTypesData.filter(t => t.id !== bulkMergeTarget.id).reduce((sum, t) => sum + (t.entries_count || 0), 0)}
                </p>
              </div>
            )}
          </div>

          <AlertDialogFooter>
            <AlertDialogCancel disabled={isBulkMerging}>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleBulkMerge}
              disabled={!bulkMergeTarget || isBulkMerging}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {isBulkMerging ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Fusionando...
                </>
              ) : (
                <>
                  <Merge className="w-4 h-4 mr-2" />
                  Fusionar {selectedTypes.size - 1} tipo{selectedTypes.size > 2 ? 's' : ''}
                </>
              )}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default MaintenanceView;
